import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './Projects.css';
import HighlightedTitle from '../components/HighlightedTitle';
import ProjectPage from '../components/ProjectPage';

function CodingProjects() {
  const projects = React.useMemo(() => [
    {
      id: 'cell-migration',
      title: 'Cell Migration Simulation',
      date: '2024',
      summary: "This project explores cell behavior during chemotaxis by simulating two migration models: the Compass model and the Split model, in varying chemoattractant gradients. Implemented in Python with Pygame visualizations, the study investigates model efficiency in diverse gradient conditions, providing insights into pseudopod dynamics and cell movement patterns.",
      images: [{ type: 'image', url: '/project_images_compressed/code/cell-migration/cell-migration.jpg' }],
      link: 'https://github.com/willhardison/cell-migration-simulation'
    },
    {
      id: 'plant-moisture-tracker',
      title: 'Plant Moisture Tracker',
      date: '2023',
      summary: "An interactive website to track your plants.",
      images: [
        { type: 'image', url: '/project_images_compressed/code/plant-moisture-tracker/plant.jpg' },
        { type: 'website', url: 'https://hardison-plants.netlify.app' },
      ],
      link: 'https://hardison-plants.netlify.app'
    },
    {
      id: 'will-fridays',
      title: 'Will Fridays',
      date: '2026',
      summary: 'A weekly challenge and rewards system designed around habit-building, playful incentives, and a simple, repeatable ritual that turns Fridays into a mini game.',
      images: [
        { type: 'image', url: '/project_images_compressed_optimized/interactive/will-fridays/will1.jpg' },
        { type: 'website', url: 'https://willfridays.com' },
      ],
      link: 'https://willfridays.com',
      writing: 'Will Fridays is a lightweight social and personal challenge platform that turns weekly goals into something playful and trackable.\n\nThe concept is simple: each Friday becomes a reset point, a chance to check in, make progress, and collect small wins. The value comes from the routine itself — repeated actions, low friction, and a system that rewards consistency.\n\nI built it as a fun project with a clear product focus: keep the experience feel-good, visual, and immediate, while making the habit loop easy to return to week after week.'
    },
    {
      id: 'training-plan-generator',
      title: 'Training Plan Generator and Tracker',
      date: '2023',
      summary: "This interactive web app, built with React and Python, generates personalized workout programs designed to help users improve their mile time. By customizing the plan based on fitness levels and goals, the app provides a structured approach to progressively enhance running performance.",
      images: [{ type: 'image', url: '/project_images_compressed/code/training-plan-generator/training.jpg' }],
      link: 'https://training-plan.netlify.app'
    },
    {
      id: 'footymetrics',
      title: 'FootyMetrics',
      date: '2022',
      summary: "This interactive platform lets users analyze and compare Premier League players by customizing stat weights to reflect their preferences. Featuring dynamic rankings, interactive visualizations, and API access, it’s a powerful tool for fans and analysts to explore player performance from the 2022/23 season.",
      images: [{ type: 'image', url: '/project_images_compressed/code/footymetrics/soccer.jpg' }, { type: 'website', url: 'https://willhardison.github.io/premiere-league-stats/' }],
      link: 'https://willhardison.github.io/premiere-league-stats/'
    },
    {
      id: 'chess-bot',
      title: 'Chess Bot',
      date: '2021',
      summary: "Challenge yourself against a beginner-friendly chess engine with an approximate 700 Elo rating. This Python-based game features a user-friendly interface, logical move validation, and a computer opponent powered by MiniMax and Alpha-Beta Pruning, evaluating moves up to two levels deep.",
      images: [{ type: 'image', url: '/project_images_compressed/code/chess-bot/chess.jpg' }],
      link: 'https://github.com/willhardison/Hardison-Chess'
    },
  ], []);

  const getSelectedProjectFromQuery = () => {
    const projectId = new URLSearchParams(window.location.search).get('project');
    return projects.find((project) => project.id === projectId) || null;
  };

  const [selectedProject, setSelectedProject] = useState(getSelectedProjectFromQuery);
  const [imageLoaded, setImageLoaded] = useState(new Array(projects.length).fill(false));

  React.useEffect(() => {
    const projectId = new URLSearchParams(window.location.search).get('project');
    setSelectedProject(projects.find((project) => project.id === projectId) || null);
  }, [projects]);

  const handleSelectProject = (project) => {
    const params = new URLSearchParams(window.location.search);
    params.set('project', project.id);
    const nextUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, '', nextUrl);
    setSelectedProject(project);
  };

  const handleCardClick = (project) => {
    if (project.id === 'will-fridays' && project.link) {
      window.open(project.link, '_blank', 'noopener,noreferrer');
      return;
    }

    handleSelectProject(project);
  };

  const handleBack = () => {
    const nextUrl = window.location.pathname;
    window.history.pushState({}, '', nextUrl);
    setSelectedProject(null);
  };

  const handleImageLoad = (index) => {
    setImageLoaded((prev) => {
      const newLoadedState = [...prev];
      newLoadedState[index] = true;
      return newLoadedState;
    });
  };

  return (
    <AnimatePresence mode="wait">
      {selectedProject ? (
        <motion.div
          key="projectPage"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.5 }}
        >
          <ProjectPage project={selectedProject} onBack={handleBack} />
        </motion.div>
      ) : (
        <motion.div
          key="projectList"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 50 }}
          transition={{ duration: 0.5 }}
        >
          <div className="projects-container">
            {projects.map((project, index) => (
              <div
                key={index}
                className={`project-item ${index % 2 === 0 ? 'left-first' : 'right-first'}`}
                onClick={() => handleCardClick(project)}
              >
                <div className={`project-image-shell ${imageLoaded[index] ? 'is-loaded' : 'is-loading'}`}>
                  <img
                    src={project.images[0].url}
                    alt={project.title}
                    className="project-image"
                    onLoad={() => handleImageLoad(index)}
                    loading="lazy"
                  />
                </div>
                <div className="project-text">
                  <div className="project-title">
                    <HighlightedTitle text={project.title} />
                    <p>{project.date}</p>
                  </div>
                  <p className='project-summary'>{project.summary}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default CodingProjects;